// Imports
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAt, faPhone, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import ContactInfo, { ContactDetails } from '../../shared-components/contact-info';
import styled from 'styled-components/macro';
import formatPhoneNumber from '../../../../../../../shareables/utils/format-phone-number';
import {
	faFacebook,
	faFlickr,
	faFoursquare,
	faInstagram,
	faLinkedinIn,
	faMedium,
	faPinterest,
	faSpotify,
	faTumblr,
	faXTwitter,
	faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import {
	GetAccountResponse,
	SocialURLType,
} from '../../../../../../../shareables/types/api/main/website/page/type/207/account/get';
import theme from '../../../../../../../shareables/theme';
import ProfilePhoto from '../../../../../shared-components/profile-photo';
import UserAvatar from '../../../../../shared-components/user-avatar';
import { useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';


// Styled components
const ProfileAvatar = styled(UserAvatar)`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.8rem;
`;

const Container = styled.div`
	display: flex;
	gap: 1rem;
	align-items: center;
	flex-wrap: wrap;
	
	img,
	${ProfileAvatar} {
		width: 3.75rem;
		height: 3.75rem;
		border-radius: 50%;
		box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
		align-self: flex-start;
		
		@media (max-width: ${theme.breakpoints.mobile}px) {
			display: none;
		}
	}
`;

const Wrapper = styled.div`
	flex: 1;
`;

const MemberDesignation = styled.em`
	color: #999;
`;

const MemberDetails = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 14rem) minmax(0, 1fr);
	column-gap: 6rem;
	margin-top: 0.625rem;
	
	a {
		color: inherit;
		text-decoration: underline;
		
		& :hover,
		:focus {
			text-decoration: none;
			color: inherit;
		}
	}
	
	.fa-phone + a {
		text-decoration: none;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-template-columns: minmax(0, 1fr);
		margin-top: 0;
	}
`;

const Contact = styled.div`
	white-space: nowrap;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		margin-top: 0.5rem;
	}
`;

const Profile = styled.div`
	overflow-wrap: anywhere;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		> div:first-of-type {
			margin-top: 0.625rem;
		}
	}
`;

const Socials = styled.div`
	a {
		margin-left: 0.625rem;
	}
`;


// Map social media type to icon
const socialIconMap: Record<SocialURLType, { icon: IconDefinition; color: string; label: string }> = {
	FACEBOOK: { icon: faFacebook, color: '#3b5998', label: 'Facebook' },
	TWITTER: { icon: faXTwitter, color: '#000', label: 'X' },
	INSTAGRAM: { icon: faInstagram, color: '#e03664', label: 'Instagram' },
	LINKEDIN: { icon: faLinkedinIn, color: '#0077b5', label: 'LinkedIn' },
	YOUTUBE: { icon: faYoutube, color: '#ff0000', label: 'YouTube' },
	PINTEREST: { icon: faPinterest, color: '#bd081c', label: 'Pinterest' },
	SPOTIFY: { icon: faSpotify, color: '#1DB954', label: 'Spotify' },
	TUMBLR: { icon: faTumblr, color: '#35465c', label: 'Tumblr' },
	MEDIUM: { icon: faMedium, color: '#00ab6c', label: 'Medium' },
	FOURSQUARE: { icon: faFoursquare, color: '#f94877', label: 'Foursquare' },
	FLICKR: { icon: faFlickr, color: '#ff0084', label: 'Flickr' },
};


// Define the accepted props
interface Props {
	member: GetAccountResponse['members'][0];
}


// Function component
const Member: React.FC<Props> = ({ member }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Initialize contact details
	const contactDetails: ContactDetails[] = [
		'email' in member && member.email
			? {
					icon: faAt,
					href: `mailto:${member.email}`,
					text: member.email,
				}
			: null,
		'phone' in member && member.phone
			? {
					icon: faPhone,
					href: `tel:${member.phone}`,
					text: formatPhoneNumber(member.phone, website?.country.callingCode ?? 1),
				}
			: null,
	].filter((contact): contact is ContactDetails => contact !== null);
	
	
	// Build profile details JSX
	const profileDetails = [
		'socials' in member && member.socials.length > 0 ? (
			<Socials key='socials'>
				<strong>Connect:</strong>
				{member.socials.map((social) => (
					<a
						key={social.url}
						href={social.url}
						target='_blank'
						rel='noopener noreferrer'
						aria-label={`${member.firstName} ${member.lastName} on ${socialIconMap[social.type].label}`}
					>
						<FontAwesomeIcon
							icon={socialIconMap[social.type].icon}
							size='lg'
							color={socialIconMap[social.type].color}
						/>
					</a>
				))}
			</Socials>
		) : null,
		'employer' in member && member.employer ? (
			<div key='employer'>
				<strong>Employer: </strong>
				{member.employer}
			</div>
		) : null,
		(member.age ?? 0) > 0 ? (
			<div key='age'>
				<strong>Age: </strong>
				{member.age}
			</div>
		) : null,
		'about' in member && member.about ? (
			<React.Fragment key='about'>
				<strong>About:</strong>
				{member.about.split('\n').map((line, index) => (
					<div key={index}>{line}</div>
				))}
			</React.Fragment>
		) : null,
	].filter((section) => section !== null);
	
	
	// Return JSX
	return (
		<Container>
			{member.profilePhotoURL ? (
				<ProfilePhoto size='3.75rem' alt='Profile photo' src={member.profilePhotoURL} />
			) : (
				<ProfileAvatar firstName={member.firstName} lastName={member.lastName} round />
			)}
			
			<Wrapper>
				<h4>
					{`${member.firstName} ${member.lastName}`}
					{'designation' in member && <MemberDesignation>({member.designation})</MemberDesignation>}
				</h4>
				
				{(contactDetails.length > 0 || profileDetails.length > 0) && (
					<MemberDetails>
						{contactDetails.length > 0 && (
							<Contact>
								<ContactInfo contactDetails={contactDetails} />
							</Contact>
						)}
						
						{profileDetails.length > 0 && <Profile>{profileDetails}</Profile>}
					</MemberDetails>
				)}
			</Wrapper>
		</Container>
	);
};

export default Member;
